import { inject, Injectable, NgZone } from "@angular/core";
import { Data } from "@angular/router";
import { getAuth } from "firebase/auth";
import { firstValueFrom, of, Subscription } from "rxjs";
import { catchError, filter, take, timeout } from "rxjs/operators";
import { CheckoutFlowService, resolvePaymentVisitorId } from "src/app/services/checkout-flow.service";
import { PaymentService } from "src/app/services/payment.service";
import { SalesJourneyIdentityService } from "src/app/services/sales-journey-identity.service";
import { environment } from "src/environments/environment";
import { v4 as uuidv4 } from "uuid";

export interface PreAuthCheckoutFlowHost {
  keys: Data;
  language: string;
  paymentMode: string;
  pathString: string;
  checkOutComData?: Data;
  userId: string;
  preferredCurrency: string;
  paymentReason: string;
  isCheckoutComform: boolean;
  isButtonDisabled: boolean;
  showCheckOutComSpinner: boolean;
  errorMessage: string;
  checkoutFlowReady?: boolean;
  onFlowPaymentCompleted?: () => void;
  onFlowReady?: () => void;
  getCheckOutComResponseSubscription?: Subscription;
}

@Injectable({ providedIn: "root" })
export class PreAuthCheckoutFlowService {
  private checkoutFlowService = inject(CheckoutFlowService);
  private paymentService = inject(PaymentService);
  private ngZone = inject(NgZone);
  private cachedDisplaySession: Data | null = null;
  private warmStartPromise: Promise<void> | null = null;
  private salesJourneyIdentityService = inject(SalesJourneyIdentityService);

  unmount(): void {
    this.checkoutFlowService.unmount();
    this.cachedDisplaySession = null;
    this.warmStartPromise = null;
  }

  submitPayment(): void {
    this.checkoutFlowService.submitPayment();
  }

  isCheckoutLoadingMessage(message: string): boolean {
    return (message || "").trim().toLowerCase() === "loading secure card form...";
  }

  /** Starts SDK download as early as possible. */
  preloadSdk(): Promise<void> {
    return this.checkoutFlowService.preloadSdk();
  }

  /**
   * Preloads SDK and display session in parallel once purchase data is available.
   */
  warmStart(host: PreAuthCheckoutFlowHost): Promise<void> {
    if (!host.keys?.publicKey || !host.checkOutComData?.customer_purchase) {
      return Promise.resolve();
    }

    if (!this.warmStartPromise) {
      const paymentData = this.buildPreAuthPaymentData(host);
      this.warmStartPromise = Promise.all([
        this.checkoutFlowService.preloadSdk(),
        this.createDisplaySession(host, paymentData).then((session) => {
          this.cachedDisplaySession = session;
        }),
      ]).then(() => undefined);
    }

    return this.warmStartPromise;
  }

  buildPreAuthPaymentData(host: PreAuthCheckoutFlowHost): Data {
    const msn = host.checkOutComData?.msn;
    const userNumber = msn?.substring(2);
    const preAuthAmount =
      host.checkOutComData?.price ??
      host.checkOutComData?.customer_purchase?.price ??
      1;
    const offerId =
      host.checkOutComData?.offer_id ??
      host.checkOutComData?.customer_purchase?.offer_id ??
      "";
    const prosubId =
      host.checkOutComData?.prosub_id ??
      localStorage.getItem("prosub_id") ??
      null;

    const paymentData = {
      amount: Number(preAuthAmount) > 0 ? Number(preAuthAmount) : 1,
      currency: host.preferredCurrency,
      email: host.checkOutComData?.email || msn + "@lottosocial.com",
      name: host.checkOutComData?.name,
      phone: msn,
      userId: host.userId,
      logId: host.pathString,
      customer_purchase: host.checkOutComData?.customer_purchase,
      msn: {
        country_code: `+${host.checkOutComData?.country_code}`,
        number: userNumber,
      },
      payment_reason: host.paymentReason,
      offer_id: offerId,
      prosub_id: prosubId,
      sub_status: "pre_auth",
      p_type: "21",
      visitorid: resolvePaymentVisitorId(),
      requestid: uuidv4(),
      return_url: window.location.href,
    };
    const auth = getAuth();
    const paymentDataWithIdentity = this.salesJourneyIdentityService.applyIdentityEnvelope(paymentData, {
      userId: auth?.currentUser?.isAnonymous ? null : (host.userId ?? null),
      anonymousId: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
      // Omit customerId: resolve the actual SQL ID when available.
      msn: msn,
      checkoutAttemptId: host.pathString ? String(host.pathString) : null,
    });
    return paymentDataWithIdentity;
  }

  async mountPreAuthCheckoutFlow(host: PreAuthCheckoutFlowHost): Promise<void> {
    if (!host.keys?.publicKey) {
      throw new Error("Checkout publicKey is not configured");
    }
    if (!host.checkOutComData?.customer_purchase) {
      throw new Error("Missing pre-auth purchase data");
    }

    host.isCheckoutComform = true;
    host.checkoutFlowReady = false;
    host.errorMessage = "Loading secure card form...";
    host.showCheckOutComSpinner = true;
    host.isButtonDisabled = true;

    if (host.pathString) {
      this.paymentService.setcheckOutComDocId({ docId: String(host.pathString) });
    }

    const paymentData = this.buildPreAuthPaymentData(host);
    const componentOptions = this.checkoutFlowService.buildComponentOptionsFromPaymentData(
      host.keys,
      paymentData as Record<string, unknown>,
    );

    const cachedSession = this.cachedDisplaySession;
    this.cachedDisplaySession = null;
    this.warmStartPromise = null;

    await this.checkoutFlowService.mountForCardEntry({
      container: "#cko-flow-container",
      publicKey: host.keys.publicKey,
      environment: this.checkoutFlowService.getFlowEnvironment(host.paymentMode, host.keys.publicKey),
      locale: this.checkoutFlowService.mapLocale(host.language || "EN-GB"),
      componentOptions,
      paymentSession: cachedSession ?? undefined,
      createDisplaySession: cachedSession ? undefined : () => this.createDisplaySession(host, paymentData),
      onReady: () => {
        this.ngZone.run(() => {
          host.checkoutFlowReady = true;
          host.showCheckOutComSpinner = false;
          host.isButtonDisabled = false;
          if (this.isCheckoutLoadingMessage(host.errorMessage)) {
            host.errorMessage = "";
          }
          host.onFlowReady?.();
        });
      },
      onPaymentCompleted: (_self, paymentResponse) => {
        this.ngZone.run(() => {
          host.showCheckOutComSpinner = true;
          host.isCheckoutComform = false;
          host.isButtonDisabled = true;
          const logId = host.pathString ? String(host.pathString) : "";
          const userId = host.userId ? String(host.userId) : "";
          const paymentId = paymentResponse?.id ?? paymentResponse?.payment_id ?? paymentResponse?.paymentId;
          if (!logId || !userId || !paymentId) {
            return;
          }

          this.redirectToCheckoutResponseHandler({
            status: "success",
            logId,
            userId,
            paymentId: String(paymentId),
            sandbox: host.paymentMode === "sandbox",
          });
        });
      },
      onError: (_self, error) => {
        this.ngZone.run(() => {
          const paymentId = error?.details?.paymentId;
          const logId = host.pathString ? String(host.pathString) : "";
          const userId = host.userId ? String(host.userId) : "";

          if (paymentId && logId && userId) {
            this.redirectToCheckoutResponseHandler({
              status: "fail",
              logId,
              userId,
              paymentId: String(paymentId),
              sandbox: host.paymentMode === "sandbox",
            });
            return;
          }

          host.errorMessage = error?.message || "Failed to load payment form";
          host.showCheckOutComSpinner = false;
          host.isButtonDisabled = true;
        });
      },
    });
  }

  private redirectToCheckoutResponseHandler(options: {
    status: "success" | "fail";
    logId: string;
    userId: string;
    paymentId: string;
    sandbox: boolean;
  }): void {
    const params = new URLSearchParams({
      status: options.status,
      logId: options.logId,
      userId: options.userId,
      "cko-payment-id": options.paymentId,
    });
    if (options.sandbox) {
      params.set("sandbox", "true");
    }

    window.location.href = `${environment.onSuccessRedirectUrl}/${environment.cloudFunctions.checkoutFlowResponseHandler}?${params}`;
  }

  waitForPaymentConfirmation(
    host: PreAuthCheckoutFlowHost,
    handlers: {
      onSuccess: (paymentResData: Data) => void;
      onFailure: () => void;
      onTimeout?: () => void;
    },
  ): Subscription {
    host.getCheckOutComResponseSubscription?.unsubscribe();

    return this.paymentService.getCheckOutComResponse$
      .pipe(
        filter((paymentResData: Data) => {
          const status = paymentResData?.payment_response?.payment_status;
          return status === "success" || status === "fail";
        }),
        take(1),
        timeout(120000),
        catchError(() => of(null)),
      )
      .subscribe((paymentResData: Data | null) => {
        this.ngZone.run(() => {
          host.showCheckOutComSpinner = false;

          if (!paymentResData?.payment_response) {
            handlers.onTimeout?.();
            handlers.onFailure();
            return;
          }

          if (paymentResData.payment_response.payment_status === "success") {
            handlers.onSuccess(paymentResData);
            return;
          }

          handlers.onFailure();
        });
      });
  }

  private async createDisplaySession(host: PreAuthCheckoutFlowHost, paymentData: Data): Promise<Data> {
    const response = await firstValueFrom(
      this.checkoutFlowService.createFlowSession(host.paymentMode, paymentData as Record<string, unknown>),
    );
    const session = this.checkoutFlowService.extractPaymentSession(response as Data);
    if (!session) {
      throw new Error("Failed to create checkout flow display session");
    }
    return session;
  }
}
